import {
    BlurEvent, Button, ChangeEvent, ClickEvent, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownItem, DropdownSelectionEvent,
    Label, LookupModelSearchEvent, LookupModelSelectionEvent, Panel, ScreenStack, SelectionEvent, Snackbar, Switch, Tab, Textbox, Toast
} from "@mcleod/components";
import {
    Api, ArrayUtil, Currency, CurrencyUtil, DateRange, DisplayType, DisplayValue, LogManager, ModelRow, ModelRowType,
    StringUtil, UrlUtil
} from "@mcleod/core";
import { UserLayoutProvider } from "@mcleod/common";
import { getDispatchControlValue, LMEGeneralSettings, LMEUserSettings } from "@mcleod/general";
import { AutogenLayoutTopMatchList } from "./autogen/AutogenLayoutTopMatchList";
import { ModelBrokeragePlanningProfile } from "./models/ModelBrokeragePlanningProfile";
import { TopMatchProfileDetail } from "./TopMatchProfileDetail";
import { ResetFormButton } from "@mcleod/components/src/page/ResetFormButton";

const log = LogManager.getLogger("lme.powerbroker.TopMatchList");

export class TopMatchList extends AutogenLayoutTopMatchList {
    private _acTimeoutHandle: number;
    private searchMovement = true;
    private _enableLoadboards = false;
    private urlParams = UrlUtil.getPropsFromUrl(window.location.href);
    private _offerRate: Currency;
    private noProfileSnackbar: Snackbar;

    get offerRate() {
        return this._offerRate
    }

    set offerRate(curr: Currency) {
        const loadboardDef = LMEGeneralSettings.getSingleton().getDispatchControl().web_def_loadboard_post_rate;
        if(loadboardDef === null ? true : loadboardDef === "T")
            this._offerRate = curr;
        else
            this._offerRate = new Currency(0);
    }

    onLoad(): void | Promise<void> {
        if (!this.isNested) {
            return this.initialize();
        }
    }

    async initialize() {
        const snackId = "snackbarTdNoProfile";
        this.citystateShipper.textCombined.text = "--";
        this.citystateConsignee.textCombined.text = "--";
        this.initializeTabs(); // Must be initialized before defaults are set
        await this.setDefaultValues();
        await this.checkProfilePermissions();
        const bpModel = new ModelBrokeragePlanningProfile();
        await bpModel.searchSingle({ id: LMEUserSettings.get()?.bp_profile }).then(response => {
            if (LMEUserSettings.get()?.tm_profile == null && response?.get("int_search_profile_id") == null) {
                const panel = new Panel();
                const label = new Label({ text: "No TopMatch profile set. Please update in your User Settings." });
                const button = new Button({
                    caption: "Open User Settings",
                    onClick: () => {
                        UserLayoutProvider.navigateTo();
                        this.noProfileSnackbar.dismiss();
                    }
                });
                panel.add(label);
                panel.add(button);
                const screen = ScreenStack.getSnackbarTarget();
                this.noProfileSnackbar = screen.getOldestSnackbar(snackId) as Snackbar;
                if (this.noProfileSnackbar == null) {
                    this.noProfileSnackbar = Snackbar.showWarningSnackbar(panel, { id: snackId, millisUntilDismissal: 5000 });
                }
            }
        });
        this.textboxMovementId.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (!StringUtil.isEmptyString(event.selection?.value)) {
                this.setMovement();
            } else {
                this.sourceMovement.clear();
                this.sourceMovement.displayDataInBoundComponents();
                const labelSummary = this.panelOverview.findComponentById("labelStopSummary") as Label;
                labelSummary.text = null;
            }
        });
        
        if (this.urlParams?.autoSearch) {
            await this.search();
        }
        this.tabsetTopMatch.addAfterTabSelectionListener((event: SelectionEvent) => {
            this.textboxQualificationStatus.enabled = event.newSelection.element.id === this.tabTopMatch.id || event.newSelection.element.id === this.tabRoutingGuides.id;
            this.textboxQualificationProfile.enabled = event.newSelection.element.id === this.tabTopMatch.id || event.newSelection.element.id === this.tabRoutingGuides.id;
            this.textboxBrokeragePlanningProfile.enabled = event.newSelection.element.id === this.tabTopMatch.id;
            this.textboxTopMatchProfile.enabled = event.newSelection.element.id === this.tabTopMatch.id;
        });
        this.switchAllowCounter.checked = true;
        const resetTopmatchForm = this.searchpanelFilter["buttonResetForm"] as ResetFormButton;
        if (resetTopmatchForm) {
            const doAfterReset = resetTopmatchForm.doAfterReset;
            resetTopmatchForm.doAfterReset = async (event) => {
                if (doAfterReset) {
                    await doAfterReset(event);
                }
                if (this.isNested) {
                    this.textboxOrderId.visible = this.textboxMovementId.visible = false; 
                }
                else if (this.urlParams?.movementId != null) {
                    await this.setDefaultValues();
                }
                else {
                    this.updateStopSummary(true);
                }
            }
        }
    }

    textboxQualificationProfileBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

    private initializeTabs() {
        this.layoutTopMatch.initialize(this);
        this.layoutLoadBoards.initialize(this);
        this.layoutRoutingGuides.initialize(this);
    }

    clearOfferDetails() {
        this.panelOfferDetails.components.forEach((component) => {
            if (component instanceof Switch) {
                component.checked = true;
            }
            else if (component instanceof Textbox) {
                component.clear();
            }
        })
        this.textboxOfferRate.visible = false;
    }

    async defaultDateRange() {
        const dateRange = DateRange.parseDateRangeWithKeywords("t-60,t");
        this.textboxDateRange.text = DisplayValue.getDisplayValue(dateRange, DisplayType.DATERANGE, this.textboxDateRange.format);
    }

    // START - Set defaults
    async setDefaultsFromDataSource(searchMovement = false): Promise<void> {
        this.searchMovement = searchMovement;
        if (this.mainDataSource?.activeRow != null) {
          await this.setDefaultValues();
        }
    }

    async checkProfilePermissions() {
        if (!StringUtil.isEmptyString(this.textboxTopMatchProfile.text)) {
            const results = await Api.search("lme/powerbroker/topmatch-profile-details", { integratedsearch_profile_id: this.textboxTopMatchProfile.text });
            if (results.data.length > 0) {
                this._enableLoadboards = false;
                results.data.forEach(row => {
                    if (row.search_type === TopMatchProfileDetail.SEARCH_TYPES.LOADBOARDS) {
                        this._enableLoadboards = true;
                    }
                })
                this.tabLoadBoards.visible = this._enableLoadboards;
            }
        }
    }

    async setDefaultValues() {
        await Promise.all([
            this.setDefaultOrderId(),
            this.setDefaultTabTitles(),
            this.setDefaultMovementId(),
            this.setDefaultTrailerType(),
            this.setDefaultTopMatchProfile(),
            this.defaultDateRange()
        ]);
    }

    async setDefaultOrderId() {
        this.textboxOrderId.text = this.activeRow?.get("movement_order.order_id") ?? this.urlParams?.orderId;
        return Promise.resolve();
    }

    async setDefaultTabTitles(): Promise<void> {
        this.layoutTopMatch.movement = null;
        this.layoutTopMatch.updateTabTitle = (rowCount: number) => this.updateTabTitle(this.tabTopMatch, "TopMatch", rowCount);
        this.layoutLoadBoards.updateTabTitle = (rowCount: number) => this.updateTabTitle(this.tabLoadBoards, "Load Boards", rowCount);
        this.layoutRoutingGuides.updateTabTitle = (rowCount: number) => this.updateTabTitle(this.tabRoutingGuides, "Routing Guides", rowCount);
        return Promise.resolve();
    }

    private updateTabTitle(tab: Tab, caption: string, rowCount: number) {
        if (rowCount != null)
            caption += ` ${rowCount}`;
        tab.caption = caption;
    }

    async setDefaultMovementId(): Promise<void> {
        const movementId = this.activeRow?.get("id") ?? this.urlParams?.movementId;
        if (movementId != null) {
            const options = [ new DropdownItem(movementId, movementId) ];
            this.textboxMovementId.items = options;
            this.textboxMovementId.selectedItem = options[0];
            await this.setMovement();
            return Promise.resolve();
        } else if (!StringUtil.isEmptyString(this.textboxOrderId.text)) {
            await this.setMovementIdOptions();
            return Promise.resolve();
        }
    }

    async setDefaultTrailerType(): Promise<void> {
        const trailerType = this.urlParams?.trailerType;
        if (trailerType != null) {
            const lmData = new ModelRow(this.textboxTrailerType.lookupModel, false, { "id": trailerType });
            lmData.type = ModelRowType.LOOKUP_MODEL_DATA;
            this.sourceTopMatch.searchRow.setLookupModelData(this.textboxTrailerType.field, lmData);
            this.sourceTopMatch.searchRow.set(this.textboxTrailerType.field, trailerType);
            this.textboxTrailerType.displayData(this.sourceTopMatch.searchRow, null, 0);
            return Promise.resolve();
        } else {
            this.setOrderTrailerType();
            return Promise.resolve();
        }
    }

    async setDefaultTopMatchProfile(tmProfile?: string) {
        if (tmProfile == null)
            tmProfile = this["tmProfile"] ?? LMEUserSettings.get().tm_profile;

        this.sourceTopMatch.searchRow.setLookupModelData(
            this.textboxTopMatchProfile.field,
            new ModelRow(this.textboxTopMatchProfile.lookupModel, false, { id: tmProfile })
        );
        this.sourceTopMatch.searchRow.set(this.textboxTopMatchProfile.field, tmProfile);
        this.textboxTopMatchProfile.displayData(this.sourceTopMatch.searchRow, null, 0);

        return Promise.resolve();
    }

    async setDefaultBrokeragePlanningProfile(bpProfile?: string) {
        if (bpProfile == null) {
            bpProfile = this.urlParams?.bpProfile ?? LMEUserSettings.get()?.bp_profile;
        }

        this.sourceTopMatch.searchRow.setLookupModelData(
            this.textboxBrokeragePlanningProfile.field,
            new ModelRow(this.textboxBrokeragePlanningProfile.lookupModel, false, { id: bpProfile })
        );
        this.sourceTopMatch.searchRow.set(this.textboxBrokeragePlanningProfile.field, bpProfile);
        this.textboxBrokeragePlanningProfile.displayData(this.sourceTopMatch.searchRow, null, 0);

        return await new ModelBrokeragePlanningProfile().searchSingle({ id: bpProfile }).then(response => {
            if (response != null) {
                const tmProfile = response.get("int_search_profile_id");
                if (tmProfile != null) {
                    this.setDefaultTopMatchProfile(tmProfile);
                }
                const qualProfile = response.get("brk_qual_profile");
                const qualStatus = response.get("brk_qual_status");
                this.setDefaultQualificationProfileAndStatus(qualProfile, qualStatus);
            }
        });
    }

    setDefaultQualificationProfileAndStatus(qualProfile: string, qualStatus: string) {
        // Set Qualification Profile for TopMatch with preference as Order > BP Profile > Dispatch Control
        qualProfile = this.sourceMovement.activeRow?.getString("orders.brk_qual_profile") ?? qualProfile ?? getDispatchControlValue("brk_qual_profile");
        if (!StringUtil.isEmptyString(qualProfile)) {
            this.sourceTopMatch.searchRow.setLookupModelData(
                this.textboxQualificationProfile.field,
                new ModelRow(this.textboxQualificationProfile.lookupModel, false, { id: qualProfile })
            );
            this.sourceTopMatch.searchRow.set(this.textboxQualificationProfile.field, qualProfile);
            this.textboxQualificationProfile.displayData(this.sourceTopMatch.searchRow, null, 0);
        }
        this.textboxQualificationStatus.setSelectedItemFromValue(qualStatus);
    }
    // END - Set defaults

    async sourceTopMatchBeforeExecution(event: DataSourceExecutionEvent) {
        if (DataSourceAction.SEARCH == event.getAction()) {
            event.preventDefault();
            await this.search();
        }
    }

    public async search(): Promise<any> {
        const searchButton = this.searchpanelFilter["buttonSearch"] as Button;
        if (searchButton) {
            searchButton.busy = true;
        }
        this.clearOfferDetails()
        this.layoutTopMatch.tableTopMatch.busy = true;
        this.layoutLoadBoards.tableLoadboards.busy = true;
        this.layoutRoutingGuides.tableRoutingGuides.busy = true;
        const offerHistoryData = (await Api.search("lme/powerbroker/offer-log", { movement_id: this.sourceMovement.activeRow.get("id") })).data[0]["offer_history"];
        const searchValues = this.sourceTopMatch.getDataboundValues(DataSourceMode.SEARCH, null, true);
        searchValues.set("offer_history", offerHistoryData)

        // Load Boards Table
        const loadboardPromise = !!this._enableLoadboards
            ? this.layoutLoadBoards.search(searchValues)
                .catch(e => log.error(e))
                .finally(() => this.layoutLoadBoards.tableLoadboards.busy = false)
            : Promise.resolve();

        // TopMatch Table
        this.layoutTopMatch.movement = this.sourceMovement;
        const topMatchSearchValues = new ModelRow(searchValues._modelPath, false, searchValues.data);
        const topMatchPromise = loadboardPromise.then(() => {
            topMatchSearchValues.set("loadboard_data", this.layoutLoadBoards.sourceLoadboards.data);
            return this.layoutTopMatch.search(topMatchSearchValues)
                .catch(e => log.error(e))
                .finally(() => this.layoutTopMatch.tableTopMatch.busy = false);
        });

        // Routing Guides
        searchValues.set("customer_id", this.sourceMovement.activeRow.get("orders.customer_id"));
        searchValues.set("commodity_id", this.sourceMovement.activeRow.get("orders.commodity_id"));
        searchValues.set("order_mode", this.sourceMovement.activeRow.get("orders.order_mode"));
        const routingGuidePromise = this.layoutRoutingGuides.search(searchValues)
            .catch(e => log.error(e))
            .finally(() => this.layoutRoutingGuides.tableRoutingGuides.busy = false);

        return await Promise.all([topMatchPromise, routingGuidePromise])
            .catch(e => {/*do nothing*/})
            .finally(() => {
                if (searchButton) {
                    searchButton.busy = false;
                }
                this.layoutTopMatch.tableTopMatch.busy = false;
                this.layoutLoadBoards.tableLoadboards.busy = false;
                this.layoutRoutingGuides.tableRoutingGuides.busy = false;
            });
    }

    async showOfferLogIcon(orderId: string) {
        Api.search("lme/powerbroker/offer-log", { "order_id": orderId, "just_log": true }).then(response => {
            const offerHistory = response.data[0]["offer_history"];
            if (!ArrayUtil.isEmptyArray(offerHistory)) {
                let offerLogs = "";
                for (let i = 0; i < offerHistory.length; i++) {
                    const offerDate = offerHistory[i]["offer_date"];
                    const numOffers = offerHistory[i]["offer_count"];
                    const offerType = offerHistory[i]["offer_type"];
                    offerLogs += `${offerDate}  ${numOffers.toString().padStart(6, ' ')} ${offerType} Offer${numOffers > 1 ? "s" : ""}\n`;
                }
                this.labelOfferLog.visible = true;
                this.labelOfferLog.tooltipDynamicContent = () => {
                    const panel = new Panel({ padding: 0, margin: 0 });
                    panel.add(new Label({ text: "Offer Log", fontSize: "xlarge", fontBold: true }));
                    panel.add(new Label({ text: offerLogs }));
                    return panel;
                }
            }
            else {
                this.labelOfferLog.visible = false;
            }
        });
    }

    textboxOrderIdOnChange(event: ChangeEvent) {
        if (event.newValue.length >= 7) {
            this.showOfferLogIcon(event.newValue);
        }

        if (!event.userInitiatedChange)
            return;

        this.textboxTrailerType.clear();

        if (this._acTimeoutHandle != null)
            window.clearTimeout(this._acTimeoutHandle);

        this._acTimeoutHandle = window.setTimeout(async () => {
            if (event.newValue.length >= 7) {
                this.setMovementIdOptions();
            }
        }, this.textboxOrderId.lookupModelInputDelay);
    }

    async setMovementIdOptions() {
        Api.post("lme/dispatch/order-movements",
            { order_id: this.textboxOrderId.text, brokerage_only: false }
        ).then(response => {
            if (response.data == null || response.data.length == 0)
                return;

            const moveIds = response.data.map(move => {
                return new DropdownItem(move.id);
            });
            this.textboxMovementId.items = moveIds;
            this.textboxMovementId.selectedItem = moveIds[0];
            this.setMovement();
        });
    }

    public async setMovement() {
        this.layoutTopMatch.clear();
        this.layoutLoadBoards.clear();
        this.layoutRoutingGuides.clear();

        if (this.searchMovement || this.sourceMovement.activeRow == null) {
            await this.sourceMovement.search({ id: this.textboxMovementId.selectedItem.value });
        }

        this.layoutTopMatch.movement = this.sourceMovement;
        this.layoutLoadBoards.movement = this.sourceMovement;
        this.layoutRoutingGuides.movement = this.sourceMovement;
        this.setOrderTrailerType();
        await this.setDefaultBrokeragePlanningProfile();
        this.updateMoneyPanelLayout();
        this.updateStopSummary();
        if (this.sourceMovement.activeRow?.getDate("stop_origin.sched_arrive_late") == null ||
            this.sourceMovement.activeRow?.getDate("stop_origin.sched_arrive_early").getDay()
            == this.sourceMovement.activeRow?.getDate("stop_origin.sched_arrive_late").getDay()) {
                this.panelOriginSameDay.visible = true;
                this.panelOriginDifDay.visible = false;
        }
        else {
            this.panelOriginDifDay.visible = true;
            this.panelOriginSameDay.visible = false;
        }
        if (this.sourceMovement.activeRow?.getDate("stop_dest.sched_arrive_late") == null ||
            this.sourceMovement.activeRow?.getDate("stop_dest.sched_arrive_early").getDay()
            == this.sourceMovement.activeRow?.getDate("stop_dest.sched_arrive_late").getDay()) {
                this.panelDestDifDay.visible = false;
                this.panelDestSameDay.visible = true;
        }
        else {
            this.panelDestDifDay.visible = true;
            this.panelDestSameDay.visible = false;
        }
    }

    waterfallStarted() {
        this.sourceMovement.search({ id: this.textboxMovementId.selectedItem.value }).then(() => {
            this.layoutTopMatch.topMatchUtil.toggleWaterfall();
            this.layoutRoutingGuides.topMatchUtil.toggleWaterfall();
            Toast.showSuccessToast("Waterfall tendering successfully started");
        });
    }

    setOrderTrailerType() {
        const trailerType = this.activeRow?.get("orders.equipment_type_id");
        if (StringUtil.isEmptyString(trailerType)) {
            this.textboxTrailerType.clear();
            return;
        }

        const lmData = new ModelRow(this.textboxTrailerType.lookupModel, false, { "id": trailerType });
        lmData.type = ModelRowType.LOOKUP_MODEL_DATA;
        this.sourceTopMatch.searchRow.setLookupModelData(this.textboxTrailerType.field, lmData);
        this.sourceTopMatch.searchRow.set(this.textboxTrailerType.field, trailerType);
        this.textboxTrailerType.displayData(this.sourceTopMatch.searchRow, null, 0);
    }                                          

    private updateStopSummary(clear = false) {
        if (this.activeRow) {
            if (clear) {
                this.labelStopSummary.text = null;
                this.panelPickupTimes.tooltip = null;
                this.panelOverview.dataSource.clear();
                this.panelOverview.dataSource.displayDataInBoundComponents();
                return;
            }

            Api.search("lme/dispatch/stop", { "movement_id": this.activeRow.get("id") }).then(response => {
                const stopCount = response.summary_data.stop_count_text;
                this.labelStopSummary.text = stopCount;
                this.layoutLoadBoards.stopCount = stopCount;
                this.layoutTopMatch.stopCount = stopCount;
                this.layoutRoutingGuides.stopCount = stopCount;
            });
        }
    }

    textboxPostedRateOnDataDisplay(event: DataDisplayEvent) {
        if (event?.rowData?.get("pnn_rate") != null)
            this.textboxOrderspnnRate.text = CurrencyUtil.formatCurrency(event?.rowData?.get("pnn_rate"));
    }

    // Method to change layout of the panel with Posted Rate, Target Pay, and Max Pay depending on whether the order has a Posted Rate
    updateMoneyPanelLayout() {
        this.textboxOrderspnnRate.visible = this.activeRow?.get("orders.pnn_rate") != null;
        this.offerRate = this.activeRow?.getCurrency("target_pay_calc");
        this.textboxCarrierPay.text = this.activeRow?.getCurrency("target_pay_calc").toString();
    }

    /** This is an event handler for the onClick event of buttonClear.  */
    buttonClearOnClick(event: ClickEvent) {
        // clear sources and summary
        this.sourceMovement.clear();
        this.sourceTopMatch.clear();

        // clear Search fields
        this.textboxOrderId.clear();
        this.textboxMovementId.clear();
        this.textboxDateRange.clear();
        this.textboxTrailerType.clear();
        this.textboxTopMatchProfile.clear();

        // Set defaults back
        this.setDefaultTopMatchProfile();
        this.defaultDateRange();
        const clearButton: Button = event.target as Button;
        clearButton.enabled = false;
        this.textboxOrderId.focus();
    }

    /** This is an event handler for the onChange event of textboxTopMatchProfile.  */
    textboxTopMatchProfileOnChange(event) {
        this.checkProfilePermissions();
    }

    async carrierAssigned() {
        if (!this.isNested) {
            await this.setMovement();
            this.search();
        } else {
            this.owner.mainDataSource.search({ id: this.sourceMovement.activeRow.get("id") });
        }
    }

    textboxBrokeragePlanningProfileOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const id = event.selection.get("id");
            this.setDefaultBrokeragePlanningProfile(id);
        }
    }

    textboxTopMatchProfileBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

    textboxPayMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        const newValue = event.selection?.value;
        switch (newValue) {
            case 'F':
                this.textboxPayUnits.text = "1.0";
                break;
            case 'D':
                this.textboxPayUnits.text = parseFloat(this.activeRow?.get("move_distance")).toFixed(1);
                break;
            case 'C':
                this.textboxPayUnits.text = (this.activeRow?.get("orders.weight", 0) / 100).toFixed(1);
                break;
            case 'T':
                this.textboxPayUnits.text = (this.activeRow?.get("orders.weight", 0) / 2000).toFixed(1)
                break;
            case 'E':
                this.textboxPayUnits.text = this.activeRow?.get("orders.pieces", 0).toFixed(1);
                break;
            default:
                break;
        }
        this.payFieldOnChange();
    }

    payFieldOnChange(event?: BlurEvent) {
        if (event == null || event.changedWhileFocused) {
            if (this.textboxPayUnits.getDataValue()) {
                this.textboxPayUnits.text = parseFloat(this.textboxPayUnits.getDataValue()).toFixed(1)
            }
            if (this.textboxRate.getDataValue()) {
                this.textboxRate.text = parseFloat(this.textboxRate.getDataValue()).toFixed(1)
            }
            const units = this.textboxPayUnits.getDataValue() as number;
            const rate = this.textboxRate.getDataValue() as number;
            const offerRate = new Currency((rate * units));
            this.offerRate = offerRate;
            this.textboxCarrierPay.text = offerRate.toString();
            this.textboxOfferRate.visible = true;
            this.textboxOfferRate.text = offerRate.toString();
            this.layoutTopMatch.topMatchUtil.updateRates(offerRate);
            this.layoutRoutingGuides.topMatchUtil.updateRates(offerRate);
        }
    }
}
